import { Link } from 'react-router-dom';
import { Pool, PoolStatus } from '../types/pool';
import { getPoolTypeMeta } from '../utils/poolTypeMeta';

interface PoolCardProps {
  pool: Pool;
  /** Whether the current user has entered this pool. Used to render the "Entered" affinity badge. */
  entered?: boolean;
  /**
   * Suppress the "Entered" badge even when entered is true.
   * Pass `false` from sections like "My Pools" where the badge would be redundant.
   * Default: true.
   */
  showEnteredBadge?: boolean;
}

/**
 * Status accent colors — used as a 4px top stripe on the card so users can
 * scan the page and pick out status without reading the small badge.
 */
const STATUS_ACCENT: Record<PoolStatus, string> = {
  [PoolStatus.OPEN]:        'bg-emerald-500',
  [PoolStatus.LOCKED]:      'bg-amber-500',
  [PoolStatus.IN_PROGRESS]: 'bg-blue-500',
  [PoolStatus.COMPLETED]:   'bg-gray-300',
  [PoolStatus.CANCELLED]:   'bg-rose-400',
};

const STATUS_LABEL: Record<PoolStatus, string> = {
  [PoolStatus.OPEN]:        'Accepting entries',
  [PoolStatus.LOCKED]:      'Picks locked',
  [PoolStatus.IN_PROGRESS]: 'In progress',
  [PoolStatus.COMPLETED]:   'Completed',
  [PoolStatus.CANCELLED]:   'Cancelled',
};

const STATUS_TEXT_COLOR: Record<PoolStatus, string> = {
  [PoolStatus.OPEN]:        'text-emerald-700',
  [PoolStatus.LOCKED]:      'text-amber-700',
  [PoolStatus.IN_PROGRESS]: 'text-blue-700',
  [PoolStatus.COMPLETED]:   'text-gray-500',
  [PoolStatus.CANCELLED]:   'text-rose-600',
};

function formatShortDate(d: Date) {
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatRelativeDeadline(deadline: Date): { text: string; tone: 'urgent' | 'soon' | 'normal' } {
  const ms = deadline.getTime() - Date.now();
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);

  if (ms < 0) return { text: 'Deadline passed', tone: 'urgent' };
  if (hours < 24) return { text: `${hours}h to enter`, tone: 'urgent' };
  if (days < 3) return { text: `${days}d to enter`, tone: 'soon' };
  if (days < 30) return { text: `${days} days to enter`, tone: 'normal' };
  return { text: `Closes ${formatShortDate(deadline)}`, tone: 'normal' };
}

export function PoolCard({ pool, entered, showEnteredBadge = true }: PoolCardProps) {
  const meta = getPoolTypeMeta(pool.type);
  const deadline = new Date(pool.deadline);
  const isCompleted = pool.status === PoolStatus.COMPLETED || pool.status === PoolStatus.CANCELLED;
  const isOpen = pool.status === PoolStatus.OPEN;
  const accent = STATUS_ACCENT[pool.status] ?? 'bg-gray-300';
  const statusLabel = STATUS_LABEL[pool.status] ?? pool.status;
  const statusTextColor = STATUS_TEXT_COLOR[pool.status] ?? 'text-gray-600';

  const relativeDeadline = isOpen ? formatRelativeDeadline(deadline) : null;
  const deadlineToneClass =
    relativeDeadline?.tone === 'urgent'
      ? 'text-rose-600'
      : relativeDeadline?.tone === 'soon'
      ? 'text-amber-600'
      : 'text-gray-500';

  return (
    <Link
      to={`/pools/${pool.slug || pool.id}`}
      aria-label={`${pool.name} — ${statusLabel}`}
      className={`group relative flex flex-col overflow-hidden rounded-xl bg-white ring-1 ring-gray-200 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:ring-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
        isCompleted ? 'opacity-90' : ''
      }`}
    >
      {/* Status accent stripe — single most legible signal of pool state */}
      <span aria-hidden="true" className={`h-1 w-full ${accent}`} />

      <div className="flex flex-1 flex-col p-3 md:p-5">
        {/* Header: type icon + name + entered badge */}
        <div className="flex items-start gap-2 md:gap-3">
          <span
            role="img"
            aria-label={meta.label}
            className={`flex h-9 w-9 md:h-11 md:w-11 flex-shrink-0 items-center justify-center rounded-lg md:rounded-xl text-lg md:text-xl ${meta.bg}`}
          >
            {meta.emoji}
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="line-clamp-2 text-sm md:text-base font-semibold leading-snug text-gray-900 group-hover:text-blue-700">
              {pool.name}
            </h3>
            <div className="mt-0.5 md:mt-1 flex items-center gap-1.5 md:gap-2 text-[11px] md:text-xs text-gray-500">
              <span className="truncate">{meta.label}</span>
              <span aria-hidden="true">•</span>
              <span className={`font-medium ${statusTextColor} truncate`}>{statusLabel}</span>
            </div>
          </div>
          {entered && showEnteredBadge && (
            <span
              title="You've entered this pool"
              className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-indigo-50 px-1.5 md:px-2 py-0.5 text-[10px] md:text-[11px] font-semibold text-indigo-700 ring-1 ring-indigo-100"
            >
              <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              Entered
            </span>
          )}
        </div>

        {/* Metrics — compact horizontal row on mobile (one line, divider-separated),
            2-column grid on desktop. flex-1 makes the CTA pin to the bottom. */}
        <div className="mt-2 md:mt-5 flex-1">
          {/* Mobile: single dense row */}
          <div className="md:hidden flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
            <span>
              <span className="text-gray-400">Entries </span>
              <span className="font-semibold text-gray-900">{pool._count?.entries ?? 0}</span>
              {pool.maxEntries ? <span className="text-gray-400">/{pool.maxEntries}</span> : null}
            </span>
            <span aria-hidden="true" className="text-gray-300">·</span>
            <span>
              <span className="text-gray-400">Fee </span>
              <span className={pool.entryFee && Number(pool.entryFee) > 0 ? 'font-semibold text-gray-900' : 'font-semibold text-gray-400'}>
                {pool.entryFee && Number(pool.entryFee) > 0 ? `$${pool.entryFee}` : 'Free'}
              </span>
            </span>
            {!isCompleted && (
              <>
                <span aria-hidden="true" className="text-gray-300">·</span>
                <span>
                  <span className="text-gray-400">{isOpen ? 'Deadline ' : 'Locked '}</span>
                  <span className="font-semibold text-gray-900">{formatShortDate(deadline)}</span>
                  {relativeDeadline && (
                    <span className={`ml-1 ${deadlineToneClass}`}>· {relativeDeadline.text}</span>
                  )}
                </span>
              </>
            )}
          </div>
          {/* Desktop: original 2-column grid layout */}
          <dl className="hidden md:grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Entries</dt>
              <dd className="mt-0.5 font-semibold text-gray-900">
                {pool._count?.entries ?? 0}
                {pool.maxEntries ? <span className="font-normal text-gray-400"> / {pool.maxEntries}</span> : null}
              </dd>
            </div>
            {pool.entryFee && Number(pool.entryFee) > 0 ? (
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Entry fee</dt>
                <dd className="mt-0.5 font-semibold text-gray-900">${pool.entryFee}</dd>
              </div>
            ) : (
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Entry fee</dt>
                <dd className="mt-0.5 font-semibold text-gray-400">Free</dd>
              </div>
            )}
            {!isCompleted && (
              <div className="col-span-2">
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">
                  {isOpen ? 'Deadline' : 'Locked at'}
                </dt>
                <dd className="mt-0.5 flex items-baseline gap-2 font-semibold text-gray-900">
                  <span>{formatShortDate(deadline)}</span>
                  {relativeDeadline && (
                    <span className={`text-xs font-normal ${deadlineToneClass}`}>
                      · {relativeDeadline.text}
                    </span>
                  )}
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* CTA — adapts to status. Compact on mobile. */}
        <div className="mt-2 md:mt-5">
          {isCompleted ? (
            <span className="inline-flex items-center gap-1 text-xs md:text-sm font-medium text-gray-500 group-hover:text-blue-700">
              View results
              <svg className="h-3.5 w-3.5 md:h-4 md:w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          ) : isOpen ? (
            <span className="inline-flex w-full items-center justify-center rounded-lg bg-blue-600 px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-semibold text-white shadow-sm transition-colors group-hover:bg-blue-700">
              {entered ? 'View pool' : 'Join pool'}
              <svg className="ml-1 h-3.5 w-3.5 md:h-4 md:w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </span>
          ) : (
            <span className="inline-flex w-full items-center justify-center rounded-lg bg-white px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-semibold text-gray-700 ring-1 ring-gray-200 transition-colors group-hover:bg-gray-50 group-hover:ring-gray-300">
              View pool
              <svg className="ml-1 h-3.5 w-3.5 md:h-4 md:w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
